import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, Building2, GraduationCap, Globe, ShieldCheck, FileText, Clock,
  Users, AlertCircle, CheckCircle2, ArrowRight, ChevronDown, Sparkles, Award,
  ClipboardCheck, Send, MessageSquare,
} from "lucide-react";

/**
 * Visually rich extension for the Training Visa (Subclass 407) page.
 * Rendered after the standard ServicePage sections when slug === "training-407".
 * All content is original StudyNet writing based on publicly available
 * Department of Home Affairs information about Subclass 407.
 */

const STREAMS = [
  {
    icon: Award,
    eyebrow: "Stream 1",
    title: "Registration & Licensing Training",
    desc: "Structured training required to obtain registration, membership or licensing necessary to practise your occupation in your home country — or to satisfy professional development obligations of an overseas employer.",
    examples: [
      "Medical practitioners undertaking Australian-based supervised clinical training",
      "Aviation, maritime or trade professionals completing recognised certification",
      "Foreign government officials completing structured occupational placements",
    ],
  },
  {
    icon: Sparkles,
    eyebrow: "Stream 2",
    title: "Occupational Skills Enhancement",
    desc: "Workplace-based training to improve skills in an eligible occupation, typically lifting you from one defined skill level to the next in a structured, supervised plan.",
    examples: [
      "Chefs, engineers, IT specialists undertaking advanced workplace training",
      "Allied health professionals upskilling for international practice",
      "Mid-career professionals exposed to advanced Australian processes",
    ],
  },
  {
    icon: Globe,
    eyebrow: "Stream 3",
    title: "Capacity-Building Programs",
    desc: "Training that forms part of a capacity-building program endorsed by a Commonwealth, state or territory government agency — often delivered under bilateral or multilateral arrangements.",
    examples: [
      "Government-to-government training exchanges",
      "Aid-funded development programmes for partner countries",
      "Pacific region capacity-building placements",
    ],
  },
];

const ELIGIBILITY = [
  { label: "At least 18 years old at the time of application." },
  { label: "Genuine intention to undertake the approved training and depart Australia (or transition lawfully) afterwards." },
  { label: "Functional English — evidenced by IELTS / PTE / TOEFL / OET / CAE, or by primary/secondary/tertiary study in English, or other accepted equivalents." },
  { label: "Skills, qualifications or work background appropriate to the nominated occupation (commonly 12+ months recent relevant experience)." },
  { label: "Adequate health insurance equivalent to OSHC for the entire stay." },
  { label: "Health and character requirements met by all applicants 18 and over." },
];

const SPONSOR = [
  { icon: Building2, title: "Approved Temporary Activities Sponsor",
    desc: "Either a business lawfully operating in Australia, or an eligible Commonwealth/state/territory agency, or a specific overseas government / educational entity." },
  { icon: ClipboardCheck, title: "Approved nomination & training plan",
    desc: "A detailed, structured training plan listing competencies to be acquired, supervision arrangements, on-the-job & off-the-job training, and clear learning outcomes with assessment milestones." },
  { icon: ShieldCheck, title: "Compliance with sponsor obligations",
    desc: "Record keeping, cooperation with monitoring, ensuring the training continues to be delivered as approved, and reporting any changes — all under the Migration Act 1958 framework." },
];

const PROCESS = [
  { icon: ClipboardCheck, title: "Eligibility review",
    desc: "We review your occupational background, English, sponsor relationship and training objective to confirm Subclass 407 is the right pathway." },
  { icon: Building2, title: "Sponsor / TAS approval",
    desc: "If the sponsor is not already a Temporary Activities Sponsor, we prepare the TAS application or refresh an expiring approval." },
  { icon: FileText, title: "Training plan & nomination",
    desc: "We draft a structured, Department-aligned training plan and lodge the formal nomination — the single biggest factor in a strong 407 outcome." },
  { icon: Send, title: "Visa application",
    desc: "We prepare the visa application (offshore or onshore) with English, health, character, insurance and supporting documents — including any family members." },
  { icon: MessageSquare, title: "Departmental queries",
    desc: "We respond to any additional information requests with targeted, evidence-based submissions to avoid avoidable delays or refusals." },
  { icon: CheckCircle2, title: "Grant & arrival",
    desc: "On grant, we provide a clear briefing on conditions, sponsor obligations, family rights and your strategic next steps." },
];

const DOCS = [
  "Valid passport, recent photograph and identity documents.",
  "Approved nomination from your Temporary Activities Sponsor.",
  "Detailed training plan with competencies, supervision and assessment milestones.",
  "Employment history evidence (references, payslips, contracts) showing 12+ months in the nominated occupation.",
  "Qualification certificates and academic transcripts.",
  "English language test results or acceptable evidence of an exemption.",
  "Health insurance certificate equivalent to OSHC for the visa period.",
  "Police clearances for every country lived in 12+ months in the past 10 years.",
  "Health examination results where required.",
  "Family member evidence — marriage / de facto / birth certificates.",
];

const FAQS = [
  { q: "How long can I stay on a Subclass 407?",
    a: "The Training Visa can be granted for up to 2 years. The actual visa period is set by reference to the approved training plan — short programmes will be granted shorter visa periods, while longer structured plans can secure the full 2-year stay." },
  { q: "Can I work outside the training activity?",
    a: "No. Work in Australia on a Subclass 407 is limited to the activities described in your approved training plan. Unrelated employment is a breach of condition and may lead to cancellation. Family members on the visa, however, generally have work rights subject to their own visa conditions." },
  { q: "Can I bring my partner and children?",
    a: "Yes. Eligible partners and dependent children can be included as secondary applicants either at the time of application or by subsequent application. Secondary applicants generally have work and study rights." },
  { q: "Do I need a job offer or do I need to be employed already?",
    a: "Subclass 407 is structured around a training plan, not an employment contract in the same sense as Subclass 482. You need a sponsor willing to deliver the training plan and supervise the placement — that sponsor may be an existing employer, a partner organisation or a host placement." },
  { q: "What English level is required?",
    a: "Functional English is required. This is typically demonstrated through an English test (e.g. IELTS overall 4.5, PTE 30) or through evidence of primary, secondary or tertiary study in English. Specific accepted scores and evidence types should be confirmed against the current Department of Home Affairs guidance." },
  { q: "What happens after the 407 if I want to keep working in Australia?",
    a: "Some 407 holders transition to Subclass 482 (Skills in Demand) if a sponsor is willing to nominate them in an eligible occupation, others move to GSM if they meet points-test and skills-assessment thresholds, and some return home as planned. Your post-407 pathway should be designed at the start, not at the end." },
  { q: "How is the training plan assessed?",
    a: "The Department looks closely at whether the training plan is structured (not a disguised job), at the appropriate level for the applicant, supervised by appropriately qualified individuals, and delivers measurable competency uplift over the training period. A weak or generic plan is the single most common cause of 407 refusal." },
];

function FaqItem({ item, isOpen, onToggle, idx }) {
  return (
    <div data-testid={`t407-faq-${idx}`} className="bg-white border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`t407-faq-toggle-${idx}`}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-4 hover:bg-[#F8F4FB] transition-colors"
      >
        <span className="font-display text-[15px] md:text-base text-[#29324A] leading-snug">{item.q}</span>
        <ChevronDown className={`h-5 w-5 text-[#C43A89] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="px-5 md:px-6 pb-5 pt-1 border-t border-slate-100 text-[13px] text-slate-700 leading-relaxed">{item.a}</div>
        </div>
      </div>
    </div>
  );
}

export default function TrainingVisa407Extras() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      {/* Three streams comparison */}
      <section data-testid="t407-streams" className="py-16 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">The three training streams</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">Choose the stream that matches your purpose.</h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Subclass 407 is built around <strong>structured training</strong> — not generic work experience. Picking the right stream up front is the single biggest predictor of a smooth approval.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {STREAMS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} data-testid={`t407-stream-${i}`} className="group bg-white border border-slate-200 hover:border-[#C43A89] transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(196,58,137,0.18)] p-6 md:p-7 flex flex-col">
                  <span aria-hidden className="block h-0.5 w-10 bg-[#C43A89] mb-5 group-hover:w-full transition-all duration-500" />
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-md mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">{s.eyebrow}</div>
                  <h3 className="font-display text-lg text-[#29324A] mb-2 leading-snug">{s.title}</h3>
                  <p className="text-[13px] text-slate-600 leading-relaxed mb-4">{s.desc}</p>
                  <div className="mt-auto pt-3 border-t border-dashed border-slate-200">
                    <div className="text-[10px] font-bold tracking-[0.18em] text-[#29324A] uppercase mb-1.5">Typical examples</div>
                    <ul className="space-y-1 text-[12px] text-slate-600 leading-relaxed">
                      {s.examples.map((e, k) => (
                        <li key={k} className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-[#C43A89] shrink-0" />{e}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eligibility checklist + Sponsor pillars */}
      <section data-testid="t407-eligibility" className="py-16 md:py-20 bg-[#F8F9FA] border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
          {/* Eligibility checklist */}
          <div className="lg:col-span-7">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">Eligibility — at a glance</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">Six things every 407 applicant must satisfy.</h2>
            <ul className="mt-7 space-y-3">
              {ELIGIBILITY.map((row, i) => (
                <li key={i} data-testid={`t407-elig-${i}`} className="flex items-start gap-3 bg-white border-l-4 border-[#C43A89] px-4 py-3 text-[13.5px] text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <span>{row.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Sponsor pillars */}
          <div className="lg:col-span-5">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">For sponsoring businesses & agencies</div>
            <h2 className="font-display text-2xl md:text-3xl text-[#29324A] tracking-tight leading-tight">Three pillars of a strong sponsor case.</h2>
            <div className="mt-7 space-y-3">
              {SPONSOR.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} data-testid={`t407-sponsor-${i}`} className="bg-white border border-slate-200 p-5">
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-md">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-display text-base text-[#29324A] leading-snug mb-1">{s.title}</div>
                        <p className="text-[12.5px] text-slate-600 leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Step-by-step timeline */}
      <section data-testid="t407-process" className="py-16 md:py-20 bg-[#F8F4FB] border-t border-slate-200 relative overflow-hidden">
        <div aria-hidden className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#C43A89]/8 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-[#29324A]/8 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">Our process</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">From first call to landing in Australia.</h2>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">A structured, six-stage workflow — every stage has a clear deliverable, written scope and fixed fee bracket so there are no surprises.</p>
          </div>

          <ol className="relative grid md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-3">
            <span aria-hidden className="hidden md:block absolute top-7 left-[5%] right-[5%] h-px bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
            {PROCESS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li key={i} data-testid={`t407-process-${i}`} className="relative bg-white border border-slate-200 p-5 md:p-6 hover:border-[#C43A89] transition-colors group flex flex-col">
                  <span aria-hidden className="absolute top-0 left-0 h-0.5 w-8 bg-[#C43A89] group-hover:w-full transition-all duration-500" />
                  <div className="relative z-10 h-14 w-14 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-lg mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">Step {i + 1}</div>
                  <div className="font-display text-base text-[#29324A] leading-snug mb-2">{s.title}</div>
                  <p className="text-[12.5px] text-slate-600 leading-relaxed">{s.desc}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Documents required */}
      <section data-testid="t407-docs" className="py-16 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">Documents</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mb-4">What you'll need to lodge.</h2>
            <p className="text-sm text-slate-700 leading-relaxed mb-4">Document quality is decisive. We provide a country-specific checklist tailored to your profile after the initial consultation, and review every document before lodgement.</p>
            <Link to="/book-consultation" className="inline-flex items-center gap-1.5 text-[#29324A] font-semibold text-sm hover:text-[#C43A89] transition-colors">Get the personalised checklist <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {DOCS.map((d, i) => (
                <li key={i} data-testid={`t407-doc-${i}`} className="flex items-start gap-2.5 bg-[#F8F4FB] border-l-2 border-[#C43A89] px-3 py-2.5 text-[13px] text-slate-700 leading-relaxed">
                  <FileText className="h-3.5 w-3.5 text-[#29324A] mt-0.5 shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="t407-faq" className="py-16 md:py-20 bg-[#F8F9FA] border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center justify-center gap-2">
              <AlertCircle className="h-3.5 w-3.5" /> Frequently asked questions
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">Training Visa — your questions, answered.</h2>
          </div>

          <div className="space-y-3">
            {FAQS.map((item, i) => (
              <FaqItem key={i} idx={i} item={item} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>

          <div className="mt-10 bg-[#29324A] text-white p-6 md:p-8 grain relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Ready to start?</div>
              <h3 className="font-display text-xl md:text-2xl leading-snug">Speak with our team about a Subclass 407 case.</h3>
            </div>
            <Link
              to="/book-consultation"
              data-testid="t407-final-cta"
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors btn-shimmer"
            >
              Book consultation <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
